import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { contactsAPI } from '../services/api';
import { Bell } from 'lucide-react';
import NavigationBar from './NavigationBar';
import EventNavigation from './EventNavigation';
import LoadingSpinner from './LoadingSpinner';
import './UserProfile.css';

const UserProfile = () => {
    const { contactId } = useParams();
    const { user } = useAuth();
    const navigate = useNavigate();
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    
    // Get the last event from localStorage for sidebar context
    const eventNameId = localStorage.getItem('last_event');

    // Check if viewing own profile
    const isOwnProfile = user?.contact_id === parseInt(contactId);

    useEffect(() => {
        const fetchProfile = async () => {
            try {
                setLoading(true);
                // Use secure public profile endpoint - excludes sensitive data like email/phone
                const response = await contactsAPI.getPublicProfile(contactId);
                setProfile(response.data);
                setError(null);
            } catch (err) {
                console.error('Error fetching profile:', err);
                setError('Failed to load profile');
            } finally {
                setLoading(false);
            }
        };

        if (contactId) {
            fetchProfile();
        }

        // Listen for profile image updates (when user uploads new image on edit page)
        const handleProfileUpdate = () => {
            if (contactId) {
                fetchProfile();
            }
        };

        window.addEventListener('profileImageUpdated', handleProfileUpdate);
        return () => window.removeEventListener('profileImageUpdated', handleProfileUpdate);
    }, [contactId]);

    // Get initials for avatar
    const getInitials = () => {
        if (!profile) return '?';
        const firstName = profile.first_name || '';
        const lastName = profile.last_name || '';
        return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || '?';
    };

    // Format full name
    const getFullName = () => {
        if (!profile) return '';
        return `${profile.first_name || ''} ${profile.last_name || ''}`.trim() || 'User';
    };

    if (loading) {
        return (
            <>
                <NavigationBar />
                <div className="event-layout">
                    {eventNameId && <EventNavigation eventNameId={eventNameId} />}
                    <div className={`profile-container ${eventNameId ? 'with-sidebar' : ''}`}>
                        <LoadingSpinner message="Loading profile..." />
                    </div>
                </div>
            </>
        );
    }

    if (error || !profile) {
        return (
            <>
                <NavigationBar />
                <div className="event-layout">
                    {eventNameId && <EventNavigation eventNameId={eventNameId} />}
                    <div className={`profile-container ${eventNameId ? 'with-sidebar' : ''}`}>
                        <div className="profile-error">
                            <h2>Error</h2>
                            <p>{error || 'Profile not found'}</p>
                            <button onClick={() => navigate(-1)} className="btn-back">
                                Go Back
                            </button>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            <NavigationBar />
            <div className="event-layout">
                {eventNameId && <EventNavigation eventNameId={eventNameId} />}
                <div className={`profile-container ${eventNameId ? 'with-sidebar' : ''}`}>
                {/* Profile Header Card */}
                <div className="profile-header-card">
                    <div className="profile-banner"></div>
                    <div className="profile-content">
                        <div className="profile-avatar-large">
                            {profile.image_url ? (
                                <img src={profile.image_url} alt={getFullName()} />
                            ) : (
                                getInitials()
                            )}
                        </div>
                        <div className="profile-info">
                            <h1 className="profile-name">{getFullName()}</h1>
                            {profile.job_title && (
                                <p className="profile-title">{profile.job_title}</p>
                            )}
                            {profile.company_name && (
                                <p className="profile-company">{profile.company_name}</p>
                            )}
                            <div className="profile-location">
                                {(profile.city || profile.state_province || profile.country) && (
                                    <span>
                                        {[profile.city, profile.state_province, profile.country]
                                            .filter(Boolean)
                                            .join(', ')}
                                    </span>
                                )}
                            </div>
                        </div>
                        {isOwnProfile && (
                            <div className="profile-actions">
                                <button
                                    className="btn-edit-profile"
                                    onClick={() => navigate(`/user/${contactId}/edit`)}
                                >
                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                        <path d="M12.854.146a.5.5 0 0 0-.707 0L10.5 1.793 14.207 5.5l1.647-1.646a.5.5 0 0 0 0-.708l-3-3zm.646 4.646L9.793 1.085 3.293 7.585A.5.5 0 0 0 3.146 8l-.5 4.5a.5.5 0 0 0 .146.353.5.5 0 0 0 .354.147H4l4.5-.5a.5.5 0 0 0 .354-.146l6.5-6.5z" fill="currentColor" />
                                    </svg>
                                    Edit Profile
                                </button>
                                <Link to="/notification-settings" className="btn-notification-settings">
                                    <Bell size={16} />
                                    Notification Settings
                                </Link>
                            </div>
                        )}
                    </div>
                </div>

                {/* About Section */}
                <div className="profile-section">
                    <div className="section-header">
                        <h2>About</h2>
                    </div>
                    <div className="section-content">
                        {profile.short_bio && (
                            <div className="profile-bio">
                                <p>{profile.short_bio}</p>
                            </div>
                        )}
                        <div className="info-grid">
                            {profile.job_title && (
                                <div className="info-item">
                                    <div className="info-label">Role</div>
                                    <div className="info-value">{profile.job_title}</div>
                                </div>
                            )}
                            {profile.company_name && (
                                <div className="info-item">
                                    <div className="info-label">Company</div>
                                    <div className="info-value">{profile.company_name}</div>
                                </div>
                            )}
                            {(profile.city || profile.state_province || profile.country) && (
                                <div className="info-item">
                                    <div className="info-label">Location</div>
                                    <div className="info-value">
                                        {[profile.city, profile.state_province, profile.country]
                                            .filter(Boolean)
                                            .join(', ')}
                                    </div>
                                </div>
                            )}
                            {profile.timezone && (
                                <div className="info-item">
                                    <div className="info-label">Time Zone</div>
                                    <div className="info-value">{profile.timezone}</div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Companies */}
                {profile.companies && profile.companies.length > 0 && (
                    <div className="profile-section">
                        <div className="section-header">
                            <h2>Companies</h2>
                        </div>
                        <div className="section-content">
                            <div className="companies-grid">
                                {profile.companies.map((company) => (
                                    <div key={company.CompanyId} className="company-card">
                                        {company.LogoUrl && (
                                            <img
                                                src={company.LogoUrl}
                                                alt={company.Name}
                                                className="company-logo"
                                            />
                                        )}
                                        <div className="company-info">
                                            <h3 className="company-name">{company.Name}</h3>
                                            {(company.Location || company.City || company.StateProvince || company.Country) && (
                                                <p className="company-location">
                                                    {company.Location ||
                                                        [company.City, company.StateProvince, company.Country]
                                                            .filter(Boolean)
                                                            .join(', ')}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {/* Social Links */}
                {(profile.linkedin_url || profile.instagram) && (
                    <div className="profile-section">
                        <div className="section-header">
                            <h2>Social Links</h2>
                        </div>
                        <div className="section-content">
                            <div className="social-links">
                                {profile.linkedin_url && (
                                    <a
                                        href={profile.linkedin_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="social-link"
                                    >
                                        <svg width="20" height="20" viewBox="0 0 20 20" fill="#0077b5">
                                            <path d="M0 1.5C0 0.67 0.67 0 1.5 0H18.5C19.33 0 20 0.67 20 1.5V18.5C20 19.33 19.33 20 18.5 20H1.5C0.67 20 0 19.33 0 18.5V1.5ZM6.17 16.67V7.92H3.33V16.67H6.17ZM4.75 6.75C5.72 6.75 6.42 6.01 6.42 5.08C6.42 4.15 5.72 3.42 4.75 3.42C3.78 3.42 3.08 4.15 3.08 5.08C3.08 6.01 3.78 6.75 4.75 6.75ZM16.67 16.67H16.67V11.83C16.67 9.33 16.11 7.42 13.17 7.42C11.75 7.42 10.83 8.17 10.42 8.88H10.38V7.92H7.67V16.67H10.5V12.33C10.5 11.08 10.75 9.88 12.33 9.88C13.88 9.88 13.92 11.29 13.92 12.42V16.67H16.67Z" />
                                        </svg>
                                        LinkedIn
                                    </a>
                                )}
                                {profile.instagram && (
                                    <a
                                        href={profile.instagram.startsWith('http') ? profile.instagram : `https://instagram.com/${profile.instagram.replace(/^@/, '')}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="social-link"
                                    >
                                        <svg width="20" height="20" viewBox="0 0 20 20" fill="#E4405F">
                                            <path d="M10 0C7.28 0 6.94 0.01 5.88 0.06C2.73 0.22 0.22 2.73 0.06 5.88C0.01 6.94 0 7.28 0 10C0 12.72 0.01 13.06 0.06 14.12C0.22 17.27 2.73 19.78 5.88 19.94C6.94 19.99 7.28 20 10 20C12.72 20 13.06 19.99 14.12 19.94C17.27 19.78 19.78 17.27 19.94 14.12C19.99 13.06 20 12.72 20 10C20 7.28 19.99 6.94 19.94 5.88C19.78 2.73 17.27 0.22 14.12 0.06C13.06 0.01 12.72 0 10 0ZM10 1.8C12.67 1.8 12.99 1.81 14.04 1.86C16.78 1.99 18.01 3.24 18.14 5.96C18.19 7.01 18.2 7.33 18.2 10C18.2 12.67 18.19 12.99 18.14 14.04C18.01 16.76 16.78 18.01 14.04 18.14C12.99 18.19 12.67 18.2 10 18.2C7.33 18.2 7.01 18.19 5.96 18.14C3.22 18.01 1.99 16.76 1.86 14.04C1.81 12.99 1.8 12.67 1.8 10C1.8 7.33 1.81 7.01 1.86 5.96C1.99 3.24 3.22 1.99 5.96 1.86C7.01 1.81 7.33 1.8 10 1.8ZM10 4.86C7.16 4.86 4.86 7.16 4.86 10C4.86 12.84 7.16 15.14 10 15.14C12.84 15.14 15.14 12.84 15.14 10C15.14 7.16 12.84 4.86 10 4.86ZM10 13.33C8.16 13.33 6.67 11.84 6.67 10C6.67 8.16 8.16 6.67 10 6.67C11.84 6.67 13.33 8.16 13.33 10C13.33 11.84 11.84 13.33 10 13.33ZM15.34 3.46C14.67 3.46 14.13 4 14.13 4.67C14.13 5.34 14.67 5.88 15.34 5.88C16.01 5.88 16.54 5.34 16.54 4.67C16.54 4 16.01 3.46 15.34 3.46Z" />
                                        </svg>
                                        Instagram
                                    </a>
                                )}
                            </div>
                        </div>
                    </div>
                )}
                </div>
            </div>
        </>
    );
};

export default UserProfile;

